<template>
  <Portal>
    <Modal
      class="stock-profit-modal"
      data-testid="stock-profit-modal"
      :is-open="isOpen"
      size="md"
      @close="$emit('on-close')"
    >
      <HeadingDisplay
        class="stock-profit-modal__header"
        :title="props.ticker"
        size="small"
      />
      <section v-if="hasError">
        <HelperViewStatus
          title="Houve um problema ao carregar o detalhe do ativo"
          description="Tente novamente mais tarde."
          button-label="Tentar novamente"
          status="alert"
          @click="getStockProfitData"
        />
      </section>
      <ContentDisplayLoader
        v-else-if="isLoading"
        data-testid="stock-profit-modal-loader"
        class="stock-profit-modal__content"
      >
        <ListDisplayLoader class="stock-profit-modal__content__value" />
        <ListDisplayLoader class="stock-profit-modal__content__value" />
        <ListDisplayLoader class="stock-profit-modal__content__value" />
      </ContentDisplayLoader>
      <ContentDisplay
        v-else
        class="stock-profit-modal__content"
        :blocks-length="1"
      >
        <template #content-block-1>
          <Heading :level="6" class="stock-profit-modal__content__header"
            >Dividend Yield</Heading
          >
          <ListDisplay
            v-for="(detail, index) in stockProfitDetail"
            :key="index"
            class="stock-profit-modal__content__value"
            :title="detail.label"
            :value="detail.value"
          />
        </template>
      </ContentDisplay>
    </Modal>
  </Portal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  Modal,
  HeadingDisplay,
  HelperViewStatus,
  ContentDisplay,
  ListDisplay,
  Heading
} from '@warrenbrasil/nebraska-web';
import { ContentDisplayLoader } from '@/modules/trade/components/ContentDisplayLoader';
import { ListDisplayLoader } from '@/modules/trade/components/ListDisplayLoader';

import { Portal } from '@/modules/common/components/Portal';
import {
  StockProfitDetail,
  getStockProfitDetail
} from '@/modules/wealth/services/portfolio';

interface Props {
  portfolioId: string;
  ticker: string;
  year: string;
  month: string;
  isOpen: boolean;
}
const props = defineProps<Props>();

const isLoading = ref(true);
const hasError = ref(false);
const stockProfitData = ref<StockProfitDetail | null>(null);

const stockProfitDetail = computed(() => {
  return [
    {
      label: 'Mês',
      value: stockProfitData.value?.monthly
    },
    {
      label: '12M',
      value: stockProfitData.value?.past12Months
    },
    {
      label: 'Do início',
      value: stockProfitData.value?.inception
    }
  ];
});

const getStockProfitData = async () => {
  isLoading.value = true;
  hasError.value = false;

  try {
    stockProfitData.value = await getStockProfitDetail(
      props.portfolioId,
      props.ticker,
      props.year,
      props.month
    );
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.isOpen,
  async () => {
    if (props.isOpen) {
      await getStockProfitData();
    }
  },
  { immediate: true }
);
</script>
<style lang="less" scoped>
:deep(.modal-content) {
  padding: @size-spacing-x600 @size-spacing-x800 @size-spacing-x1000
    @size-spacing-x800;
  text-align: left;
}

.text-bold {
  font-weight: bold;
}

.stock-profit-modal {
  &__header {
    margin-bottom: @size-spacing-x600;
    align-self: self-start;
  }

  &__content {
    width: 100%;

    &__header {
      margin-bottom: @size-spacing-x500;
    }

    &__loading {
      margin: @size-spacing-x300 @size-spacing-x400 0 @size-spacing-x400;
    }

    &__loading:last-child {
      margin: @size-spacing-x300 @size-spacing-x400 @size-spacing-x500
        @size-spacing-x400;
    }

    &__value {
      margin-bottom: @size-spacing-x350;

      &:last-child {
        margin: 0;
      }
    }
  }
}
</style>
